import React, { useEffect, useState } from "react";
import { CalculatorInput } from "@/components/ui/calculator-input";
import { Checkbox } from "@/components/ui/checkbox";
import StepCard from "../StepCard";
import ImageWithFallback from "../ImageWithFallback";
import { useIsMobile } from "@/hooks/use-mobile";
import { Separator } from "@/components/ui/separator";
import InstructionCheckbox from "./InstructionCheckbox";

interface InitialFlowInputProps {
  selectedSlicer: "creality" | "orca" | "anycubic";
  initialFlowRatio: string;
  onInitialFlowRatioChange: (value: string) => void;
  onNext: () => void;
  onPrevious: () => void;
  currentPass?: number;
  previousFlowRatio?: string;
  previousFlowRatioOriginal?: string;
  previousFlowRatioFormatted?: string;
}

const InitialFlowInput = ({ 
  selectedSlicer, 
  initialFlowRatio, 
  onInitialFlowRatioChange, 
  onNext, 
  onPrevious,
  currentPass = 1,
  previousFlowRatio,
  previousFlowRatioOriginal,
  previousFlowRatioFormatted
}: InitialFlowInputProps) => {
  const [toggleState, setToggleState] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);
  const [confirmOutOfRange, setConfirmOutOfRange] = useState(false);
  const [checkedSteps, setCheckedSteps] = useState<Record<string, boolean>>({});
  
  // Get current theme from document
  const getCurrentTheme = () => {
    if (typeof document !== 'undefined') {
      return document.documentElement.getAttribute('data-theme') || 'light';
    }
    return 'light';
  };

  const theme = getCurrentTheme();
  const isMobile = useIsMobile();

  // Toggle animation for Creality Print advanced view
  useEffect(() => {
    const interval = setInterval(() => {
      setToggleState(prev => !prev);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Auto-fill previous flow ratio for pass 2 - use the formatted calculated value
  useEffect(() => {
    if (currentPass === 2 && previousFlowRatioFormatted && !initialFlowRatio) {
      onInitialFlowRatioChange(previousFlowRatioFormatted);
    }
  }, [currentPass, previousFlowRatioFormatted, initialFlowRatio, onInitialFlowRatioChange]);

  const handleStepCheck = (stepId: string, checked: boolean) => {
    setCheckedSteps(prev => ({
      ...prev,
      [stepId]: checked
    }));
  };

  // Normalize decimal separators to standard decimal point
  const normalizeDecimalSeparator = (value: string) => {
    // Replace comma, apostrophe, and Arabic decimal separator with decimal point
    return value.replace(/[,''٫]/g, '.');
  };

  const validateInitialFlow = (value: string) => {
    const normalizedValue = normalizeDecimalSeparator(value);
    const num = parseFloat(normalizedValue);
    if (isNaN(num)) return false;
    if (num > 1.2) return "• Value seems too high.\n\n• It is recommended to start with a value of 1.\n\n• A value below 0.8 may cause underextrusion.\n\n• A value higher than 1.2 is likely to cause overextrusion.";
    if (num < 0.8) return "• Value seems too low.\n\n• It is recommended to start with a value of 1.\n\n• A value below 0.8 may cause underextrusion.\n\n• A value higher than 1.2 is likely to cause overextrusion.";
    return true;
  };

  const isValueOutOfRange = () => {
    if (!initialFlowRatio) return false;
    const normalizedValue = normalizeDecimalSeparator(initialFlowRatio);
    const num = parseFloat(normalizedValue);
    return !isNaN(num) && (num > 1.2 || num < 0.8);
  };

  const getFlowRatioStatus = () => {
    if (!initialFlowRatio) return "neutral";
    const normalizedValue = normalizeDecimalSeparator(initialFlowRatio);
    const value = parseFloat(normalizedValue);
    if (value > 1.2 || value < 0.8) return "warning";
    
    // Check if Pass 2 and input differs from previous flow ratio
    if (currentPass === 2 && previousFlowRatioFormatted) {
      const normalizedPrevious = normalizeDecimalSeparator(previousFlowRatioFormatted);
      if (Math.abs(value - parseFloat(normalizedPrevious)) > 0.001) {
        return "orange";
      }
    }
    
    return "good";
  };

  const canProceedFromInitialFlow = () => {
    if (!initialFlowRatio) return false;
    if (!showValidationError) return true; // Allow proceeding if validation hasn't been triggered
    const validation = validateInitialFlow(initialFlowRatio);
    if (validation === true) return true;
    return confirmOutOfRange; // Can proceed if user confirmed out-of-range value
  };

  const handleNext = () => {
    setShowValidationError(true);
    const validation = validateInitialFlow(initialFlowRatio);
    if (validation === true || confirmOutOfRange) {
      onNext();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleNext();
    }
  };

  const handleFlowRatioChange = (value: string) => {
    // Allow numbers, decimal point, comma, apostrophe, and Arabic decimal separator
    const regex = /^[0-9]*[.,''٫]?[0-9]*$/;
    if (value === '' || regex.test(value)) {
      onInitialFlowRatioChange(value);
    }
  };

  // Format the flow ratio to remove trailing zeros
  const formatFlowRatio = (value: string) => {
    const normalizedValue = normalizeDecimalSeparator(value);
    const num = parseFloat(normalizedValue);
    return isNaN(num) ? value : num.toString();
  };

  const getSlicerInstructions = () => {
    if (selectedSlicer === "creality") {
      const pencilIcon = theme === "dark" 
        ? "/lovable-uploads/dd96e8e3-7df4-42a5-95ba-c7e4b6dcffee.png"
        : "/lovable-uploads/3ad1fd6e-5eab-4799-b95d-d5e7744fb0da.png";

      return (
        <div className="space-y-4">
          <ol className="space-y-3">
            <InstructionCheckbox 
              id="creality-input-step0"
              checked={checkedSteps["creality-input-step0"] || false}
              onCheckedChange={(checked) => handleStepCheck("creality-input-step0", checked)}
            >
              Click the <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>name of the filament</span> in the <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>top right corner</span> of Creality Print.
            </InstructionCheckbox>
            <Separator className="my-3 bg-gray-300" />
            <InstructionCheckbox 
              id="creality-input-step1"
              checked={checkedSteps["creality-input-step1"] || false}
              onCheckedChange={(checked) => handleStepCheck("creality-input-step1", checked)}
            >
              <span>
                Click the{" "}
                <ImageWithFallback
                  src={pencilIcon}
                  alt="pencil icon" 
                  className="inline h-4 w-4 mx-1"
                />
                <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>pencil icon</span> <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>on the right</span> to open the <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>filament settings</span> window.
              </span>
            </InstructionCheckbox>
            <Separator className="my-3 bg-gray-300" />
            <InstructionCheckbox 
              id="creality-input-step2"
              checked={checkedSteps["creality-input-step2"] || false}
              onCheckedChange={(checked) => handleStepCheck("creality-input-step2", checked)}
            >
              <span>
                Click the <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Advanced</span>{" "}
                <ImageWithFallback
                  src={toggleState ? "/images/toggle_on.svg" : "/images/toggle_off.svg"} 
                  alt={toggleState ? "Toggle On" : "Toggle Off"} 
                  className="inline h-4 w-8 mx-1"
                />
                button to enable <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>advanced view</span>.
              </span>
            </InstructionCheckbox>
            <Separator className="my-3 bg-gray-300" />
            <InstructionCheckbox 
              id="creality-input-step3"
              checked={checkedSteps["creality-input-step3"] || false}
              onCheckedChange={(checked) => handleStepCheck("creality-input-step3", checked)}
            >
              Look for "<span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Flow Ratio</span>" in the <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Basic Information</span> section of the <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Filament</span> tab.
            </InstructionCheckbox>
          </ol>
          
          {currentPass === 2 && previousFlowRatioFormatted && (
            <div className={`border rounded-lg p-3 ${theme === 'dark' ? 'bg-blue-950 border-blue-800' : 'bg-blue-100 border-blue-200'}`}>
              <p className={`text-sm ${theme === 'dark' ? 'text-blue-200' : 'text-blue-700'}`}>
                The last tuned flow ratio was {previousFlowRatioFormatted}
              </p>
            </div>
          )}
        </div>
      );
    }

    if (selectedSlicer === "orca") {
      const settingsIcon = theme === "dark" 
        ? "/lovable-uploads/1cecdf5a-1cce-47b0-9874-da48393ffd35.png"
        : "/lovable-uploads/6b382450-183c-4be5-a078-ff6a8650bd1c.png";

      return (
        <div className="space-y-4">
          <ol className="space-y-3">
            <InstructionCheckbox 
              id="orca-input-step1"
              checked={checkedSteps["orca-input-step1"] || false}
              onCheckedChange={(checked) => handleStepCheck("orca-input-step1", checked)}
            >
              <span className="flex items-center flex-wrap">
                Click the{" "}
                <ImageWithFallback
                  src={settingsIcon}
                  alt="Filament settings" 
                  className="inline h-4 w-4 mx-1"
                  fallbackSrc="/lovable-uploads/d9a2d93c-64e1-4434-b9a9-15447141ac0e.png"
                />
                <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>pencil icon</span> on the right of the <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>filament name</span>.
              </span>
            </InstructionCheckbox>
            <Separator className="my-3 bg-gray-300" />
            <InstructionCheckbox 
              id="orca-input-step2"
              checked={checkedSteps["orca-input-step2"] || false}
              onCheckedChange={(checked) => handleStepCheck("orca-input-step2", checked)}
            >
              Enable <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Advanced</span> view in the top right corner of the <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Material Settings</span> popup window.
            </InstructionCheckbox>
            <Separator className="my-3 bg-gray-300" />
            <InstructionCheckbox 
              id="orca-input-step3"
              checked={checkedSteps["orca-input-step3"] || false}
              onCheckedChange={(checked) => handleStepCheck("orca-input-step3", checked)}
            >
              Look for "<span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Flow Ratio</span>" in the <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Flow ratio and Pressure Advance</span> section of the <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Filament</span> tab.
            </InstructionCheckbox>
          </ol>
          
          {currentPass === 2 && previousFlowRatioFormatted && (
            <div className={`border rounded-lg p-3 ${theme === 'dark' ? 'bg-blue-950 border-blue-800' : 'bg-blue-100 border-blue-200'}`}>
              <p className={`text-sm ${theme === 'dark' ? 'text-blue-200' : 'text-blue-700'}`}>
                The last tuned flow ratio was {previousFlowRatioFormatted}
              </p>
            </div>
          )}
        </div>
      );
    }

    if (selectedSlicer === "anycubic") {
      const settingsIcon = theme === "dark" 
        ? "/lovable-uploads/1cecdf5a-1cce-47b0-9874-da48393ffd35.png"
        : "/lovable-uploads/6b382450-183c-4be5-a078-ff6a8650bd1c.png";

      return (
        <div className="space-y-4">
          <ol className="space-y-3">
            <InstructionCheckbox 
              id="anycubic-input-step1"
              checked={checkedSteps["anycubic-input-step1"] || false}
              onCheckedChange={(checked) => handleStepCheck("anycubic-input-step1", checked)}
            >
              <span className="flex items-center flex-wrap">
                Click the{" "}
                <ImageWithFallback
                  src={settingsIcon}
                  alt="Filament settings" 
                  className="inline h-4 w-4 mx-1"
                  fallbackSrc="/lovable-uploads/d9a2d93c-64e1-4434-b9a9-15447141ac0e.png"
                />
                <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>pencil icon</span> on the right of the <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>filament name</span>.
              </span>
            </InstructionCheckbox>
            <Separator className="my-3 bg-gray-300" />
            <InstructionCheckbox 
              id="anycubic-input-step2"
              checked={checkedSteps["anycubic-input-step2"] || false}
              onCheckedChange={(checked) => handleStepCheck("anycubic-input-step2", checked)}
            >
              Enable <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Advanced</span> view in the top right corner of the <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Material Settings</span> popup window.
            </InstructionCheckbox>
            <Separator className="my-3 bg-gray-300" />
            <InstructionCheckbox 
              id="anycubic-input-step3"
              checked={checkedSteps["anycubic-input-step3"] || false}
              onCheckedChange={(checked) => handleStepCheck("anycubic-input-step3", checked)}
            >
              Look for "<span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Flow Ratio</span>" in the <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Flow ratio and Pressure Advance</span> section of the <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Filament</span> tab.
            </InstructionCheckbox>
          </ol>
          
          {currentPass === 2 && previousFlowRatioFormatted && (
            <div className={`border rounded-lg p-3 ${theme === 'dark' ? 'bg-blue-950 border-blue-800' : 'bg-blue-100 border-blue-200'}`}>
              <p className={`text-sm ${theme === 'dark' ? 'text-blue-200' : 'text-blue-700'}`}>
                The last tuned flow ratio was {previousFlowRatioFormatted}
              </p>
            </div>
          )}
        </div>
      );
    }
  };

  const getInputBorderClass = () => {
    if (!showValidationError || !initialFlowRatio) return "border-gray-400 dark:border-gray-600";
    
    const status = getFlowRatioStatus();
    if (status === "warning") return "border-red-500";
    if (status === "orange") return "border-orange-500";
    return "border-gray-400 dark:border-gray-600";
  };

  const validationError = showValidationError && initialFlowRatio && validateInitialFlow(initialFlowRatio) !== true 
    ? validateInitialFlow(initialFlowRatio)
    : null;

  const getNextButtonClass = () => {
    if (isValueOutOfRange() && showValidationError && !confirmOutOfRange) {
      return "bg-red-500 hover:bg-red-600 text-white";
    }
    return "";
  };

  const getNextButtonText = () => {
    if (!initialFlowRatio) return "Enter Value";
    if (showValidationError && isValueOutOfRange() && !confirmOutOfRange) return "Are you sure?";
    return canProceedFromInitialFlow() ? "Next" : "Enter Value";
  };

  const shouldShowNextButton = () => {
    if (!initialFlowRatio) return false; // Hide if no input
    if (isValueOutOfRange() && showValidationError && !confirmOutOfRange) return false; // Hide "Are you sure?" button until confirmed
    return canProceedFromInitialFlow(); // Show if value is valid or confirmed
  };

  const shouldShowAreYouSureButton = () => {
    return isValueOutOfRange() && showValidationError && confirmOutOfRange;
  };

  const getTitle = () => {
    if (currentPass === 2) {
      return "Input the last tuned flow ratio";
    }
    return "Input the initial flow ratio";
  };

  return (
    <StepCard
      title=""
      onNext={handleNext}
      onPrevious={onPrevious}
      showPrevious
      nextButtonText={getNextButtonText()}
      nextButtonClassName={getNextButtonClass()}
      nextDisabled={false}
      showNext={shouldShowNextButton() || shouldShowAreYouSureButton()}
    >
      <div className="space-y-4">
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-3">
          <h3 className="text-lg font-semibold text-left text-gray-900 dark:text-white">{getTitle()}</h3>
        </div>
        
        <div className="space-y-3">
          <div>
            {getSlicerInstructions()}
          </div>
        </div>

        {selectedSlicer === "creality" && currentPass === 1 && (
          <div className={`border rounded-lg p-3 ${theme === 'dark' ? 'bg-blue-950 border-blue-800' : 'bg-blue-100 border-blue-200'}`}>
            <p className={`text-sm ${theme === 'dark' ? 'text-blue-200' : 'text-blue-700'}`}>
              This is usually close to 1.
            </p>
          </div>
        )}

        {selectedSlicer === "orca" && currentPass === 1 && (
          <div className={`border rounded-lg p-3 ${theme === 'dark' ? 'bg-blue-950 border-blue-800' : 'bg-blue-100 border-blue-200'}`}>
            <p className={`text-sm ${theme === 'dark' ? 'text-blue-200' : 'text-blue-700'}`}>
              This is usually close to 1.
            </p>
          </div>
        )}

        {selectedSlicer === "anycubic" && currentPass === 1 && (
          <div className={`border rounded-lg p-3 ${theme === 'dark' ? 'bg-blue-950 border-blue-800' : 'bg-blue-100 border-blue-200'}`}>
            <p className={`text-sm ${theme === 'dark' ? 'text-blue-200' : 'text-blue-700'}`}>
              This is usually close to 1.
            </p>
          </div>
        )}

        <div className="flex items-center gap-4">
          <div className="space-y-2 flex-1">
            <div className="flex items-center gap-3">
              <label className="text-base font-semibold">
                Flow Ratio
              </label>
            </div>
            <input
              id="initial-flow"
              type="text"
              inputMode="decimal"
              placeholder=""
              value={initialFlowRatio}
              onChange={(e) => handleFlowRatioChange(e.target.value)}
              onKeyPress={handleKeyPress}
              className={`w-full text-lg px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-black ${getInputBorderClass()}`}
              autoFocus
            />
          </div>
        </div>
        
        {validationError && (
          <div className="space-y-3">
            <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-800 dark:text-red-200 whitespace-pre-line">{validationError}</p>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="confirm-range" 
                checked={confirmOutOfRange}
                onCheckedChange={(checked) => setConfirmOutOfRange(checked as boolean)}
              />
              <label htmlFor="confirm-range" className="text-sm">
                I want to use this value anyway
              </label>
            </div>
          </div>
        )}

        {currentPass === 2 && previousFlowRatioFormatted && initialFlowRatio && 
         Math.abs(parseFloat(normalizeDecimalSeparator(initialFlowRatio)) - parseFloat(normalizeDecimalSeparator(previousFlowRatioFormatted))) > 0.001 && (
          <div className="bg-orange-50 dark:bg-orange-950 border border-orange-200 dark:border-orange-800 rounded-lg p-3">
            <p className="text-sm text-orange-800 dark:text-orange-200">
              Are you sure? The last tuned flow rate was {previousFlowRatioFormatted}
            </p>
          </div>
        )}
      </div>
    </StepCard>
  );
};

export default InitialFlowInput;
